import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { theme, cardStyle, inputStyle, btnStyle, labelStyle } from '../theme';
import { Shield, Plus, X, FileText } from 'lucide-react';

// GovSchemesPage — PMJAY / CGHS / RGHS empanelment + claims (Phase 4 / item #12).
export default function GovSchemesPage({ API_BASE, patientsList = [] }) {
  const [tab, setTab] = useState('claims');
  const [schemes, setSchemes] = useState([]);
  const [claims, setClaims]   = useState([]);
  const [summary, setSummary] = useState(null);
  const [showSchemeForm, setShowSchemeForm] = useState(false);
  const [showClaimForm,  setShowClaimForm]  = useState(false);
  const [schemeForm, setSchemeForm] = useState({ code: '', name: '', type: 'central', region: 'India' });
  const [claimForm,  setClaimForm]  = useState({ scheme: '', patientId: '', beneficiaryNo: '', treatmentName: '', estimatedCost: 0 });

  const load = useCallback(async () => {
    try {
      const [s, c, sum] = await Promise.all([
        axios.get(`${API_BASE}/gov/schemes`),
        axios.get(`${API_BASE}/gov/claims`),
        axios.get(`${API_BASE}/gov/claims/summary`),
      ]);
      setSchemes(s.data || []); setClaims(c.data || []); setSummary(sum.data);
    } catch { /* silent */ }
  }, [API_BASE]);

  useEffect(() => { load(); }, [load]);

  const saveScheme = async () => {
    try { await axios.post(`${API_BASE}/gov/schemes`, schemeForm); setShowSchemeForm(false); setSchemeForm({ code:'', name:'', type:'central', region:'India' }); load(); }
    catch (e) { alert(e.response?.data?.error || e.message); }
  };
  const saveClaim = async () => {
    try {
      const p = patientsList.find(x => x._id === claimForm.patientId);
      await axios.post(`${API_BASE}/gov/claims`, { ...claimForm, patientName: p?.name });
      setShowClaimForm(false); setClaimForm({ scheme:'', patientId:'', beneficiaryNo:'', treatmentName:'', estimatedCost:0 }); load();
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const transition = async (claimId, status) => {
    const note = window.prompt(`Note for ${status} (optional):`) || '';
    try { await axios.post(`${API_BASE}/gov/claims/${claimId}/transition`, { status, note }); load(); }
    catch (e) { alert(e.message); }
  };

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ margin: 0, color: 'var(--color-text)' }}>🏛️ Government Schemes</h2>
        <div style={{ display: 'flex', gap: 6 }}>
          {['claims','schemes'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              style={{ padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600,
                background: tab === t ? 'var(--color-accent)' : 'var(--color-bg-inset)',
                color: tab === t ? 'white' : 'var(--color-text)' }}>
              {t === 'claims' ? 'Claims' : 'Schemes'}
            </button>
          ))}
        </div>
      </div>

      {summary && tab === 'claims' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 14 }}>
          {Object.entries(summary.pipeline).map(([k, v]) => (
            <div key={k} style={{ ...cardStyle, padding: 10 }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase' }}>{k.replace(/([A-Z])/g, ' $1').trim()}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--color-text)' }}>{v}</div>
            </div>
          ))}
          <div style={{ ...cardStyle, padding: 10, gridColumn: 'span 2' }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase' }}>Total Reimbursed</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: theme.success }}>₹{(summary.reimbursedTotal || 0).toLocaleString('en-IN')}</div>
          </div>
        </div>
      )}

      {tab === 'schemes' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
            {!showSchemeForm && <button onClick={() => setShowSchemeForm(true)} style={{ ...btnStyle, marginTop: 0 }}><Plus size={14}/> Add Scheme</button>}
          </div>
          {showSchemeForm && (
            <div style={{ ...cardStyle, marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <strong>+ New Scheme</strong>
                <button onClick={() => setShowSchemeForm(false)} style={{ background:'transparent', border:'none', cursor:'pointer' }}><X size={16}/></button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div><label style={labelStyle}>Code *</label><input style={inputStyle} value={schemeForm.code} onChange={e => setSchemeForm({...schemeForm, code: e.target.value.toUpperCase()})} placeholder="PMJAY / CGHS / RGHS"/></div>
                <div><label style={labelStyle}>Name *</label><input style={inputStyle} value={schemeForm.name} onChange={e => setSchemeForm({...schemeForm, name: e.target.value})} placeholder="Ayushman Bharat..."/></div>
                <div><label style={labelStyle}>Type</label>
                  <select style={inputStyle} value={schemeForm.type} onChange={e => setSchemeForm({...schemeForm, type: e.target.value})}>
                    {['central','state','employer'].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div><label style={labelStyle}>Region</label><input style={inputStyle} value={schemeForm.region} onChange={e => setSchemeForm({...schemeForm, region: e.target.value})}/></div>
              </div>
              <button onClick={saveScheme} style={{ ...btnStyle, marginTop: 12 }}>Save</button>
            </div>
          )}
          <div style={{ ...cardStyle }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr>
                <Th>Code</Th><Th>Name</Th><Th>Type</Th><Th>Region</Th><Th>Empanelled</Th>
              </tr></thead>
              <tbody>{schemes.map(s => (
                <tr key={s._id} style={{ borderTop: '1px solid var(--color-border)' }}>
                  <Td><strong>{s.code}</strong></Td><Td>{s.name}</Td><Td>{s.type}</Td><Td>{s.region}</Td>
                  <Td>{s.empanelled ? <span style={{ color: theme.success }}>✓ Yes</span> : <span style={{ color: theme.textMuted }}>No</span>}</Td>
                </tr>
              ))}{schemes.length === 0 && <tr><td colSpan={5} style={{ padding: 20, textAlign:'center', color:'var(--color-text-tertiary)' }}>No schemes added yet.</td></tr>}</tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'claims' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
            {!showClaimForm && <button onClick={() => setShowClaimForm(true)} style={{ ...btnStyle, marginTop: 0 }}><Plus size={14}/> New Claim</button>}
          </div>
          {showClaimForm && (
            <div style={{ ...cardStyle, marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <strong>+ New Claim</strong>
                <button onClick={() => setShowClaimForm(false)} style={{ background:'transparent', border:'none', cursor:'pointer' }}><X size={16}/></button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div><label style={labelStyle}>Scheme *</label>
                  <select style={inputStyle} value={claimForm.scheme} onChange={e => setClaimForm({...claimForm, scheme: e.target.value})}>
                    <option value="">Select</option>
                    {schemes.map(s => <option key={s._id} value={s._id}>{s.code} — {s.name}</option>)}
                  </select>
                </div>
                <div><label style={labelStyle}>Patient *</label>
                  <select style={inputStyle} value={claimForm.patientId} onChange={e => setClaimForm({...claimForm, patientId: e.target.value})}>
                    <option value="">Select</option>
                    {patientsList.map(p => <option key={p._id} value={p._id}>{p.name} ({p.phone})</option>)}
                  </select>
                </div>
                <div><label style={labelStyle}>Beneficiary No.</label><input style={inputStyle} value={claimForm.beneficiaryNo} onChange={e => setClaimForm({...claimForm, beneficiaryNo: e.target.value})} placeholder="PMJAY ID / CGHS card"/></div>
                <div><label style={labelStyle}>Treatment</label><input style={inputStyle} value={claimForm.treatmentName} onChange={e => setClaimForm({...claimForm, treatmentName: e.target.value})}/></div>
                <div><label style={labelStyle}>Estimated Cost (₹)</label><input type="number" style={inputStyle} value={claimForm.estimatedCost} onChange={e => setClaimForm({...claimForm, estimatedCost: Number(e.target.value)})}/></div>
              </div>
              <button onClick={saveClaim} style={{ ...btnStyle, marginTop: 12 }}>Save Draft</button>
            </div>
          )}
          <div style={{ ...cardStyle }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr>
                <Th>Patient</Th><Th>Scheme</Th><Th>Treatment</Th><Th>Estimated</Th><Th>Reimbursed</Th><Th>Status</Th><Th>Actions</Th>
              </tr></thead>
              <tbody>{claims.map(c => (
                <tr key={c._id} style={{ borderTop: '1px solid var(--color-border)' }}>
                  <Td>{c.patientName}</Td>
                  <Td>{c.scheme?.code || c.schemeCode}</Td>
                  <Td>{c.treatmentName}</Td>
                  <Td>₹{(c.estimatedCost || 0).toLocaleString('en-IN')}</Td>
                  <Td>₹{(c.reimbursedAmount || 0).toLocaleString('en-IN')}</Td>
                  <Td><Pill text={(c.status || '').replace(/_/g, ' ')}/></Td>
                  <Td><Actions claim={c} onTransition={transition}/></Td>
                </tr>
              ))}{claims.length === 0 && <tr><td colSpan={7} style={{ padding: 20, textAlign:'center', color:'var(--color-text-tertiary)' }}>No claims yet.</td></tr>}</tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

const Th = ({ children }) => (<th style={{ textAlign:'left', padding: 8, fontSize: 11, color:'var(--color-text-secondary)', textTransform:'uppercase' }}>{children}</th>);
const Td = ({ children }) => (<td style={{ padding: 8, color:'var(--color-text)' }}>{children}</td>);

const Pill = ({ text }) => {
  const map = { 'draft':'#6b7280', 'pre auth requested':'#3b82f6', 'pre auth approved':'#10b981', 'treatment done':'#8b5cf6', 'claim submitted':'#0ea5e9', 'reimbursed':'#16a34a', 'rejected':'#dc2626', 'pre auth rejected':'#dc2626', 'cancelled':'#6b7280' };
  const color = map[text] || '#6b7280';
  return <span style={{ padding:'2px 8px', borderRadius: 10, background: color+'22', color, fontSize: 11, fontWeight: 700, textTransform:'capitalize' }}>{text}</span>;
};

const Actions = ({ claim, onTransition }) => {
  const next = {
    'draft':              ['pre_auth_requested', 'cancelled'],
    'pre_auth_requested': ['pre_auth_approved', 'pre_auth_rejected'],
    'pre_auth_approved':  ['treatment_done'],
    'treatment_done':     ['claim_submitted'],
    'claim_submitted':    ['reimbursed', 'rejected'],
  }[claim.status] || [];
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {next.map(n => (
        <button key={n} onClick={() => onTransition(claim._id, n)}
          style={{ padding: '3px 8px', fontSize: 10.5, background: 'var(--color-accent-soft)', color: 'var(--color-accent)', border: '1px solid var(--color-accent-border)', borderRadius: 6, cursor: 'pointer' }}>
          → {n.replace(/_/g, ' ')}
        </button>
      ))}
    </div>
  );
};
